/**
 * Events store - manages event state with optimistic updates, IndexedDB cache,
 * offline sync queue, and conflict-aware remote change handling.
 *
 * Conflict handling: when a local mutation (create/update/delete) is in flight
 * and a remote WebSocket change arrives for the same entity, the remote change
 * is deferred. After the API call completes, the deferred change is compared
 * against the API response using updated_at timestamps. The newer version wins
 * (last-write-wins). If the remote version overwrites the local optimistic
 * update, a conflict notification is added for the UI to display.
 */

import { create } from 'zustand';
import type { Event, CreateEventInput, UpdateEventInput } from '@anna-calendar/shared';
import * as api from '../api/client';
import { cacheEvents, getCachedEvents, cacheEvent, removeCachedEvent, enqueueMutation } from '../db/indexeddb';
import { useAuthStore } from './auth';
import { useAppStore } from './app';
import { useSyncStore } from './sync';
import { useConflictsStore } from './conflicts';
import { getQueuedMutations } from '../db/indexeddb';

interface EventsState {
  events: Event[];
  isLoading: boolean;
  error: string | null;

  fetchEvents: (params?: { start?: string; end?: string }) => Promise<void>;
  createEvent: (data: CreateEventInput) => Promise<Event | null>;
  updateEvent: (id: string, data: UpdateEventInput) => Promise<Event | null>;
  deleteEvent: (id: string) => Promise<boolean>;
  /** Apply a change from WebSocket sync */
  applyRemoteChange: (action: string, event: Event | { id: string }) => void;
  /** Load from IndexedDB when offline */
  loadFromCache: (params?: { start?: string; end?: string }) => Promise<void>;
}

export const useEventsStore = create<EventsState>((set, get) => ({
  events: [],
  isLoading: false,
  error: null,

  fetchEvents: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const events = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.fetchEvents(token, params),
      );
      set({ events, isLoading: false });
      await cacheEvents(events);
    } catch (err: unknown) {
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        set({ isLoading: false, error: 'Session expired' });
        return;
      }
      if (err instanceof api.ApiError && err.status === 401) {
        set({ isLoading: false, error: 'Session expired' });
        return;
      }
      // Offline fallback: load from cache
      const cached = await getCachedEvents(params);
      set({ events: cached, isLoading: false, error: null });
    }
  },

  createEvent: async (data) => {
    // Optimistic: add to local state immediately
    const tempId = crypto.randomUUID();
    const optimistic: Event = {
      ...data,
      id: tempId,
      user_id: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    set((s) => ({ events: [...s.events, optimistic] }));
    await cacheEvent(optimistic);

    try {
      const created = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.createEvent(token, data),
      );
      // Replace optimistic entry with server response
      set((s) => ({
        events: s.events.map((e) => (e.id === tempId ? created : e)),
      }));
      await removeCachedEvent(tempId);
      await cacheEvent(created);
      return created;
    } catch (err) {
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        set((s) => ({ events: s.events.filter((e) => e.id !== tempId) }));
        await removeCachedEvent(tempId);
        return null;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('POST', '/api/events', { ...data, id: tempId });
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      set({ error: 'Event saved locally. Will sync when connected.' });
      return optimistic;
    }
  },

  updateEvent: async (id, data) => {
    const prev = get().events.find((e) => e.id === id);
    // Optimistic update
    set((s) => ({
      events: s.events.map((e) =>
        e.id === id ? { ...e, ...data, updated_at: new Date().toISOString() } : e
      ),
    }));

    // Register pending mutation so applyRemoteChange defers incoming changes
    const conflicts = useConflictsStore.getState();
    conflicts.addPending(id);

    try {
      const updated = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.updateEvent(token, id, data),
      );

      // Apply the API response only if it's newer than what we have locally.
      // A remote change may have arrived while the request was in flight with
      // a newer updated_at, in which case the remote version wins.
      const current = get().events.find((e) => e.id === id);
      if (!current || updated.updated_at >= current.updated_at) {
        set((s) => ({
          events: s.events.map((e) => (e.id === id ? updated : e)),
        }));
        await cacheEvent(updated);
      }

      // Process any deferred remote change that arrived while the API was in flight
      processDeferredChange(id, 'event');

      conflicts.removePending(id);
      return updated;
    } catch (err) {
      conflicts.removePending(id);
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        if (prev) {
          set((s) => ({ events: s.events.map((e) => (e.id === id ? prev : e)) }));
        }
        processDeferredChange(id, 'event');
        return null;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('PUT', `/api/events/${id}`, data);
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      processDeferredChange(id, 'event');
      return prev ? { ...prev, ...data } : null;
    }
  },

  deleteEvent: async (id) => {
    const prev = get().events.find((e) => e.id === id);
    // Optimistic remove
    set((s) => ({ events: s.events.filter((e) => e.id !== id) }));

    const conflicts = useConflictsStore.getState();
    conflicts.addPending(id);

    try {
      await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.deleteEvent(token, id),
      );
      await removeCachedEvent(id);
      conflicts.removePending(id);
      return true;
    } catch (err) {
      conflicts.removePending(id);
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        if (prev) set((s) => ({ events: [...s.events, prev] }));
        return false;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('DELETE', `/api/events/${id}`, null);
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      await removeCachedEvent(id);
      return true;
    }
  },

  applyRemoteChange: (action, eventOrId) => {
    const conflicts = useConflictsStore.getState();

    if (action === 'create' || action === 'update') {
      const event = eventOrId as Event;

      // If a local mutation is in flight for this entity, defer the remote change
      if (conflicts.isPending(event.id)) {
        conflicts.deferChange(event.id, {
          entity: 'event',
          action,
          data: event as unknown as Record<string, unknown>,
          timestamp: event.updated_at,
        });
        return;
      }

      // Check if this remote update overwrites a recent local optimistic update
      set((s) => {
        const idx = s.events.findIndex((e) => e.id === event.id);
        if (idx >= 0) {
          const local = s.events[idx];
          // If the local version has a newer updated_at, the remote change is stale
          if (local.updated_at > event.updated_at) return s;
          // If the local version differs and was recently modified, notify user
          if (local.title !== event.title || local.description !== event.description ||
              local.start_time !== event.start_time || local.end_time !== event.end_time ||
              local.color !== event.color) {
            conflicts.addConflict({
              entityType: 'event',
              entityTitle: event.title,
            });
          }
          const next = [...s.events];
          next[idx] = event;
          return { events: next };
        }
        return { events: [...s.events, event] };
      });
      cacheEvent(event);
    } else if (action === 'delete') {
      const { id } = eventOrId as { id: string };

      // If a local mutation is in flight, defer the delete
      if (conflicts.isPending(id)) {
        conflicts.deferChange(id, {
          entity: 'event',
          action: 'delete',
          data: { id } as Record<string, unknown>,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      set((s) => ({ events: s.events.filter((e) => e.id !== id) }));
      removeCachedEvent(id);
    }
  },

  loadFromCache: async (params) => {
    const cached = await getCachedEvents(params);
    set({ events: cached });
  },
}));

/**
 * Process a deferred remote change for an entity after its local mutation
 * completed. Applies the deferred change if it has a newer timestamp.
 */
function processDeferredChange(entityId: string, entityType: 'event'): void {
  const conflicts = useConflictsStore.getState();
  const deferred = conflicts.popDeferred(entityId);
  if (!deferred) return;

  if (deferred.action === 'delete') {
    useEventsStore.getState().applyRemoteChange('delete', { id: entityId });
  } else {
    const remoteData = deferred.data as unknown as Event;
    const local = useEventsStore.getState().events.find((e) => e.id === entityId);
    // Only apply if the remote version is actually newer
    if (!local || remoteData.updated_at >= local.updated_at) {
      if (local && (local.title !== remoteData.title || local.description !== remoteData.description)) {
        conflicts.addConflict({
          entityType,
          entityTitle: remoteData.title,
        });
      }
      useEventsStore.getState().applyRemoteChange(deferred.action, remoteData);
    }
  }
}
